import { inject, Injectable } from '@angular/core';
import { IItemSharingSettingsDialogData, IItemSharingSettingsDialogResult } from './fi-item-sharing-settings';
import { FiDialogsService } from '@fi-shared/fi-dialogs/fi-dialogs.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiItemSharingSettingsDialogComponent } from './sharing-settings-dialog.component';
import { IFiDialogResult } from '@fi-app/shared/fi-dialogs/fi-dialogs';
import {
    IAccessRightCoreFilter,
    IAccessRightFilterCollection,
    IAccessRightSelectionFiltersCollection,
    IAccessRightUserFilter,
} from '@fi-app/shared/models/access-right-filter-collection.model';
import { isEqual } from '@fi-app/shared/utils/is-equal';
import {
    AccessRightCoreFilterModel,
    AccessRightUserFilterModel,
} from '@fi-shared/models/access-right-filter-collection.model';

@Injectable()
export class FiItemSharingSettingsDialogService {
    private appTextService = inject(AppTextService);
    private fiDialogsService = inject(FiDialogsService);

    public openItemSharingSettingsDialog(
        data: IItemSharingSettingsDialogData,
        headerText: string,
    ): IFiDialogResult<IItemSharingSettingsDialogResult> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiItemSharingSettingsDialogComponent,
            contentComponentData: data,
            headerText,
            yesButtonText: this.appTextService.get('done'),
            isNewButtonsUI: false,
            hideDialogTopBorder: true,
            headerDividerClass: 'BrandedBorderColor',
            preventUppercaseButtonLabel: true,
            isFooterDividerShown: false,
            isHeaderDividerShown: true,
            config: {
                closeOnNavigation: false,
                maxWidth: '775px',
                maxHeight: '90%',
                autoFocus: false,
                panelClass: [
                    'fi-rounded-dialog-panel',
                    'fi-rounded-dialog-full-round-buttons',
                    'fi-sharing-settings-dialog-panel',
                ],
            },
        });
    }

    getSavedFilterBySharingSelection(
        currentAccessRightCoreFilter: IAccessRightCoreFilter,
        savedFiltersCollections: IAccessRightSelectionFiltersCollection[],
        doRolIdsCheck?: boolean,
    ): IAccessRightSelectionFiltersCollection {
        return savedFiltersCollections.find(({ SelectionFilterSettingCollection }) => {
            const savedFilter = SelectionFilterSettingCollection.AccessRightCoreFilter;
            return this.isAccessRightFilterEqualToFilterModel(currentAccessRightCoreFilter, savedFilter, doRolIdsCheck);
        });
    }

    getSavedFilterBySharingSelectionWithUserIds(
        currentAccessRightFilterCollection: IAccessRightFilterCollection,
        savedFiltersCollections: IAccessRightSelectionFiltersCollection[],
        doRoleIdsCheck?: boolean,
    ): IAccessRightSelectionFiltersCollection {
        return savedFiltersCollections.find(({ SelectionFilterSettingCollection }) => {
            const savedFilter = SelectionFilterSettingCollection.AccessRightCoreFilter;
            const savedUserFilter = SelectionFilterSettingCollection.AccessRightUserFilter;
            return (
                this.isAccessRightFilterEqualToFilterModel(
                    currentAccessRightFilterCollection.AccessRightCoreFilter,
                    savedFilter,
                    doRoleIdsCheck,
                ) &&
                this.areAccessRightFilterCollectionsEqual(
                    currentAccessRightFilterCollection.AccessRightUserFilter,
                    savedUserFilter,
                    ['UserIds'],
                )
            );
        });
    }

    isAccessRightFilterEqualToFilterModel(
        accessRightCoreFilter: IAccessRightCoreFilter,
        filterModel: AccessRightCoreFilterModel,
        doRoleIdsCheck?: boolean,
    ): boolean {
        const keysToCheck = ['BusinessAreaIds', 'AreaIds', 'BusinessUnitIds'];
        if (doRoleIdsCheck) {
            keysToCheck.push('RoleIds');
        }
        return this.areAccessRightFilterCollectionsEqual(accessRightCoreFilter, filterModel, keysToCheck);
    }

    private areAccessRightFilterCollectionsEqual(
        accessRightFilter: IAccessRightUserFilter | IAccessRightCoreFilter,
        filterModel: AccessRightCoreFilterModel | AccessRightUserFilterModel,
        keysToCheck: string[],
    ): boolean {
        return keysToCheck.every((key) => {
            const accessRightFilterArray: number[] = [...(accessRightFilter[key] || [])].sort();
            const filterModelArray: number[] = [...(filterModel[key] || [])].sort();
            return isEqual(accessRightFilterArray, filterModelArray);
        });
    }
}
